/**
 * Shared notification utilities for all Clareefai apps.
 * - Play sound on new notification
 * - Update document title with unread count
 * - Request browser notification permission and show native notifications
 */

let notificationSoundContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!notificationSoundContext) {
    notificationSoundContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  }
  return notificationSoundContext;
}

/** Play a short notification sound (no external file needed). */
export function playNotificationSound(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const resume = ctx.state === 'suspended' ? ctx.resume() : Promise.resolve();
    resume.then(() => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.frequency.value = 880;
      oscillator.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.15);
    });
  } catch {
    // Ignore audio errors (e.g. autoplay policy)
  }
}

/** Update document title with unread count, e.g. "Clareefai Vendor (3)" */
export function setDocumentTitleWithCount(appName: string, unreadCount: number): void {
  if (typeof document === 'undefined') return;
  document.title = unreadCount > 0 ? `${appName} (${unreadCount})` : appName;
}

/** Request browser notification permission. Must be called from a user gesture (e.g. click) for best results. */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

/** Check if we can request permission (permission is still 'default'). */
export function canRequestNotificationPermission(): boolean {
  if (typeof window === 'undefined' || !('Notification' in window)) return false;
  return Notification.permission === 'default';
}

/** Show a native browser notification if permission granted. */
export function showBrowserNotification(title: string, options?: { body?: string; tag?: string }): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const n = new Notification(title, {
      body: options?.body ?? '',
      tag: options?.tag ?? 'clareefai-notification',
      requireInteraction: false,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Ignore
  }
}
